import basicattentiontoken from "@/assets/images/basic-attention-token.svg";
import { LandingPageAppBar } from "@/components/AppBar/LandingPageAppBar";
import { BottomSwoop } from "@/components/Assets/BottomSwoop";
import { TopSwoop } from "@/components/Assets/TopSwoop";
import { Background } from "@/components/Background/Background";
import {
  useTrackMatomoEvent,
  useTrackMatomoPageView,
} from "@/hooks/useTrackWithMatomo";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { Redirect, Link as RouterLink } from "react-router-dom";

interface Props {
  reroute: boolean;
}

export function BasicAttentionTokenLandingPage({ reroute }: Props) {
  useTrackMatomoPageView({
    documentTitle: "Basic Attention Token Landing Page",
  });

  if (reroute) {
    // Not fully removing page (2024-08-23), but discounts need further evaluation
    window.open("https://basicattentiontoken.org/", "_blank", "noopener");
    return <Redirect to="/" />;
  }

  return (
    <Background>
      <LandingPageAppBar />

      <Box mt={2} />
      <TopSwoop />
      <Box display="flex" justifyContent="center" bgcolor="white" width="100%">
        <Stack maxWidth={1000} spacing={3} mb={1}>
          <Box alignSelf="center">
            <Box
              component="img"
              src={basicattentiontoken}
              width={{ xs: "300px", md: "500px" }}
            />
          </Box>
          <Typography variant="h4" textAlign="center" fontWeight={400}>
            Pay with BAT to get <strong>50% off</strong> your first self-managed
            campaign.
          </Typography>

          <Typography variant="subtitle1" textAlign="center">
            Brave is where early adopters thrive. We&rsquo;re celebrating our
            crypto roots by offering new and returning Brave Ads customers half
            off their first self-managed campaign if they buy with BAT
            <strong>*</strong>
          </Typography>

          <Typography variant="caption" textAlign="center">
            *Limited time only. Available to new advertisers, and those who ran
            campaigns prior to September 30, 2023. Eligible for Push
            Notification ads.
          </Typography>

          <LaunchCampaignButton />
        </Stack>
      </Box>
      <BottomSwoop />

      <WhatIsBAT />
    </Background>
  );
}

function LaunchCampaignButton() {
  const { trackMatomoEvent } = useTrackMatomoEvent();

  return (
    <Stack direction="row" spacing={5} alignSelf="center" alignItems="center">
      <Button
        variant="contained"
        color="primary"
        size="medium"
        component={RouterLink}
        to="/register/browser"
        onClick={() =>
          trackMatomoEvent("basic-attention-token", "launch-campaign")
        }
        sx={{
          width: "300px",
          maxHeight: { xs: "40px", md: "60px" },
          mb: 1,
          fontSize: "16px",
        }}
      >
        Start a campaign with BAT
      </Button>
    </Stack>
  );
}

function WhatIsBAT() {
  const { trackMatomoEvent } = useTrackMatomoEvent();

  return (
    <Box display="flex" justifyContent="center" pb={5}>
      <Stack maxWidth={1000} spacing={3}>
        <Typography
          variant="h4"
          textAlign="center"
          fontWeight={600}
          color="white"
        >
          What’s BAT?
        </Typography>

        <Typography variant="subtitle1" textAlign="center" color="white">
          The{" "}
          <Link
            color="secondary"
            sx={{ cursor: "pointer" }}
            onClick={() => {
              trackMatomoEvent("basic-attention-token", "learn-about-bat");
              window.open(
                "https://basicattentiontoken.org/",
                "_blank",
                "noopener",
              );
            }}
          >
            Basic Attention Token (BAT)
          </Link>{" "}
          is Brave&rsquo;s native crypto token. Brave Browser users who opt into{" "}
          <Link
            color="secondary"
            sx={{ cursor: "pointer" }}
            onClick={() => {
              trackMatomoEvent("brave-rewards", "learn-about-brave-rewards");
              window.open(
                "https://brave.com/brave-rewards/",
                "_blank",
                "noopener",
              );
            }}
          >
            Brave Rewards
          </Link>{" "}
          can earn BAT just for opting into viewing ads from the Brave Ads
          network. Earners can hold BAT like any other crypto asset, trade it,
          cash it in, or even donate it to their favorite creators.
        </Typography>

        <Typography variant="subtitle1" textAlign="center" color="white">
          BAT holders can now also cash in their tokens to advertise their
          companies or products on the Brave Ads network.
        </Typography>
      </Stack>
    </Box>
  );
}
